import { ComponentType, createElement, lazy, LazyExoticComponent } from 'react';

interface RobustLazyConfig {
  maxRetries?: number;
  retryDelay?: number;
  reloadOnFinalFailure?: boolean;
}

type ComponentProps<T> = T extends ComponentType<infer P> ? P : never;

const isChunkLoadError = (error: unknown): boolean => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  return (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('ChunkLoadError')
  );
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function robustLazy<T extends ComponentType<any>, P extends Record<string, any>>(
  importFn: () => Promise<{ default: T }>,
  configOrProps?: RobustLazyConfig | P,
  config?: RobustLazyConfig
): LazyExoticComponent<T> | LazyExoticComponent<ComponentType<Omit<ComponentProps<T>, keyof P>>> {
  const hasDefaultProps =
    configOrProps &&
    typeof configOrProps === 'object' &&
    !(
      'maxRetries' in configOrProps ||
      'retryDelay' in configOrProps ||
      'reloadOnFinalFailure' in configOrProps
    );

  const defaultProps = hasDefaultProps ? (configOrProps as P) : undefined;
  const finalConfig = hasDefaultProps ? config || {} : (configOrProps as RobustLazyConfig) || {};

  const maxRetries = finalConfig.maxRetries ?? 3;
  const retryDelay = finalConfig.retryDelay ?? 1000;
  const reloadOnFinalFailure = finalConfig.reloadOnFinalFailure ?? true;

  const retryImport = async (attempt = 0): Promise<{ default: T }> => {
    try {
      return await importFn();
    } catch (error) {
      if (!isChunkLoadError(error) || attempt >= maxRetries) {
        if (attempt >= maxRetries && reloadOnFinalFailure) {
          window.location.reload();
          return new Promise(() => {});
        }
        throw error;
      }

      await delay(retryDelay * (attempt + 1));
      return retryImport(attempt + 1);
    }
  };

  if (defaultProps) {
    // Enhanced import with default props injection using the .then() pattern
    const enhancedImport = () =>
      retryImport().then(module => ({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        default: (props: any) => createElement(module.default, { ...defaultProps, ...props }),
      }));
    return lazy(enhancedImport) as LazyExoticComponent<
      ComponentType<Omit<ComponentProps<T>, keyof P>>
    >;
  }

  return lazy(retryImport);
}
